import React, { useState } from 'react';
import { authFetch } from '../services/api';

export default function SecurityWarningModal({ jobId, findings, onAcknowledge, onClose }) {
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState('');

  const severityColor = { CRITICAL: '#dc2626', HIGH: '#f97316', MEDIUM: '#eab308', LOW: '#6b7280' };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    setError('');
    try {
      const res = await authFetch(`/api/jobs/${jobId}/acknowledge-security`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to acknowledge');
      onAcknowledge();
    } catch (e) {
      setError(e.message);
    } finally {
      setAcknowledging(false);
    }
  };

  const highFindings = findings.filter(f => f.severity === 'HIGH' || f.severity === 'CRITICAL');

  return (
    <div style={s.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <span style={s.title}>🛡️ Security Scan — Vulnerabilities Found</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <div style={s.body}>
          <div style={s.warningBox}>
            <span style={{ fontSize: 24 }}>⚠️</span>
            <div>
              <p style={s.warningTitle}>Deployment blocked — {highFindings.length} HIGH/CRITICAL issue{highFindings.length !== 1 ? 's' : ''} found</p>
              <p style={s.warningSub}>Review the findings below. You can override this block if you understand the risks.</p>
            </div>
          </div>

          <div style={s.findingsList}>
            {findings.slice(0, 15).map((f, i) => (
              <div key={i} style={s.finding}>
                <span style={{ ...s.badge, background: severityColor[f.severity] || '#6b7280' }}>{f.severity}</span>
                <div style={s.findingContent}>
                  <span style={s.findingFile}>{f.file}:{f.line}</span>
                  <span style={s.findingMsg}>{f.message}</span>
                  <span style={s.findingTool}>{f.tool} · {f.rule_id}</span>
                </div>
              </div>
            ))}
            {findings.length > 15 && <p style={s.moreText}>+ {findings.length - 15} more findings...</p>}
          </div>

          {error && <p style={s.error}>⚠ {error}</p>}

          <div style={s.actions}>
            <button onClick={handleAcknowledge} disabled={acknowledging} style={s.dangerBtn}>
              {acknowledging ? '⏳ Processing...' : '⚠️ I understand the risks, deploy anyway'}
            </button>
            <button onClick={onClose} style={s.secondaryBtn}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: '100%', maxWidth: 600, maxHeight: '80vh', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)' },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' },
  body: { display: 'flex', flexDirection: 'column', gap: 16, padding: 24, overflowY: 'auto' },
  warningBox: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14, background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 10 },
  warningTitle: { fontSize: 14, fontWeight: 700, color: '#f97316', margin: '0 0 4px' },
  warningSub: { fontSize: 12, color: 'var(--text-muted)', margin: 0 },
  findingsList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' },
  finding: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: 8 },
  badge: { fontSize: 10, fontWeight: 700, color: '#fff', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' },
  findingContent: { display: 'flex', flexDirection: 'column', gap: 2 },
  findingFile: { fontSize: 11, fontFamily: 'monospace', color: '#a5b4fc' },
  findingMsg: { fontSize: 12, color: 'var(--text-primary)' },
  findingTool: { fontSize: 10, color: 'var(--text-muted)' },
  moreText: { fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 8 },
  error: { fontSize: 13, color: '#f87171', background: 'rgba(248,113,113,0.08)', padding: '10px 14px', borderRadius: 8, margin: 0 },
  actions: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  dangerBtn: { padding: '11px 20px', background: 'linear-gradient(135deg, #dc2626, #f97316)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  secondaryBtn: { padding: '11px 20px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
};
